import { watch } from 'vue'
import type { PipeIntegration, PipeIntegrationDef } from '..'
import type { PipeInterface } from '@render/composables/usePipe'
import { getAgeDecade } from '@render/lib/ageCalc'
import {
  formatLocalIsoDate,
  getDaysUntil,
  getMonthsFromDays,
  getWeeksFromDays,
} from '@render/lib/dateCalc'
import {
  GOAL_PACE_KG_VALUES,
  clampGoalPaceKg,
  getBmi,
  getDaysTillGoalDate,
  getDesiredWeightDiffKg,
  getFasterProgressDate,
  getFirstWeekDiffKg,
  getFirstWeekLossKg,
  getGoalDate,
  getGoalDate1kg,
  getMaxSuggestedWeightKg,
  getMinSuggestedWeightKg,
  getMonthsTillGoalDate,
  getOneMonthWeightLossKg,
  getSuggestedWeightKg,
  getWeightLostByWeeksKg,
  percentOfCurrentWeight,
} from '@render/lib/weightCalc'

const HEIGHT_KEY = 'var.global.heightCm'
const WEIGHT_KEY = 'var.global.weightKg'
const TARGET_WEIGHT_KEY = 'var.global.targetWeightKg'
const SUGGESTED_TARGET_WEIGHT_KEY = 'var.global.suggestedTargetWeightKg'
const MIN_SUGGESTED_WEIGHT_KEY = 'var.global.minSuggestedWeightKg'
const MAX_SUGGESTED_WEIGHT_KEY = 'var.global.maxSuggestedWeightKg'
const GOAL_PACE_KEY = 'var.global.goalPaceKg'
const CURRENT_BMI_KEY = 'var.global.currentBmi'
const TARGET_BMI_KEY = 'var.global.targetBmi'
const TARGET_GOAL_DATE_KEY = 'var.global.targetGoalDate'
const TARGET_GOAL_DATE_1KG_KEY = 'var.global.targetGoalDate1kgPerWeek'
const DAYS_TILL_GOAL_DATE_KEY = 'var.global.daysTillGoalDate'
const MONTHS_TILL_GOAL_DATE_KEY = 'var.global.monthsTillGoalDate'
const ONE_MONTH_WEIGHT_LOSS_KG_KEY = 'var.global.oneMonthWeightLossKg'
const DESIRED_WEIGHT_DIFF_KG_KEY = 'var.global.desiredWeightDiffKg'
const FASTER_GOAL_DATE_KEY = 'var.global.fasterGoalDate'
const FIRST_WEEK_LOSS_KG_KEY = 'var.global.firstWeekLossKg'
const FIRST_WEEK_DIFF_KG_KEY = 'var.global.firstWeekDiffKg'
const PERCENT_OF_CURRENT_WEIGHT_KEY = 'var.global.percentOfCurrentWeight'
const AGE_KEY = 'var.global.age'
const AGE_GROUP_DECADE_KEY = 'var.global.ageGroupDecade'
const EVENT_DATE_KEY = 'var.global.eventDate'
const DAYS_UNTIL_EVENT_KEY = 'var.global.daysUntilEvent'
const WEEKS_UNTIL_EVENT_KEY = 'var.global.weeksUntilEvent'
const MONTHS_UNTIL_EVENT_KEY = 'var.global.monthsUntilEvent'
const WEIGHT_LOST_BY_EVENT_KG_KEY = 'var.global.weightLostByEventKg'

const useFitnessFunctionsIntegration: PipeIntegration = (): PipeIntegrationDef => ({
  setup(pipe: PipeInterface) {
    watch(
      () => pipe.getData(HEIGHT_KEY),
      (raw) => {
        const heightCm = Number(raw)
        if (!Number.isFinite(heightCm) || heightCm <= 0) return
        pipe.setData(SUGGESTED_TARGET_WEIGHT_KEY, getSuggestedWeightKg({ heightCm }))
      },
      { immediate: true },
    )

    watch(
      () => pipe.getData(GOAL_PACE_KEY),
      (raw) => {
        const n = Number(raw)
        if (!Number.isFinite(n) || n <= 0) return
        if ((GOAL_PACE_KG_VALUES as readonly number[]).includes(n)) return
        pipe.setData(GOAL_PACE_KEY, clampGoalPaceKg(n))
      },
    )

    watch(
      () => [pipe.getData(WEIGHT_KEY), pipe.getData(HEIGHT_KEY)],
      ([w, h]) => {
        const bmi = getBmi({ weightKg: Number(w), heightCm: Number(h) })
        if (bmi === null) return
        pipe.setData(CURRENT_BMI_KEY, bmi)
      },
      { immediate: true },
    )

    watch(
      () => [pipe.getData(TARGET_WEIGHT_KEY), pipe.getData(HEIGHT_KEY)],
      ([w, h]) => {
        const bmi = getBmi({ weightKg: Number(w), heightCm: Number(h) })
        if (bmi === null) return
        pipe.setData(TARGET_BMI_KEY, bmi)
      },
      { immediate: true },
    )

    watch(
      () => pipe.getData(HEIGHT_KEY),
      (raw) => {
        const min = getMinSuggestedWeightKg({ heightCm: Number(raw) })
        if (min !== null) pipe.setData(MIN_SUGGESTED_WEIGHT_KEY, min)
      },
      { immediate: true },
    )

    watch(
      () => [pipe.getData(HEIGHT_KEY), pipe.getData(WEIGHT_KEY)],
      ([h, w]) => {
        const max = getMaxSuggestedWeightKg({
          heightCm: Number(h),
          weightKg: Number(w),
        })
        if (max !== null) pipe.setData(MAX_SUGGESTED_WEIGHT_KEY, max)
      },
      { immediate: true },
    )

    watch(
      () => [
        pipe.getData(WEIGHT_KEY),
        pipe.getData(TARGET_WEIGHT_KEY),
        pipe.getData(GOAL_PACE_KEY),
      ],
      ([w, t, p]) => {
        const weightKg = Number(w)
        const targetWeightKg = Number(t)
        if (!Number.isFinite(weightKg) || weightKg <= 0) return
        if (!Number.isFinite(targetWeightKg) || targetWeightKg <= 0) return
        const paceNum = Number(p)
        const goalPaceKg = Number.isFinite(paceNum) && paceNum > 0 ? paceNum : undefined
        const input = { weightKg, targetWeightKg, goalPaceKg }
        pipe.setData(DAYS_TILL_GOAL_DATE_KEY, getDaysTillGoalDate(input))
        pipe.setData(MONTHS_TILL_GOAL_DATE_KEY, getMonthsTillGoalDate(input))
        // Dates are formatted on the visitor's local calendar (consistent with
        // daysTillGoalDate above) and skipped rather than written when the
        // computed Date is somehow invalid — `formatLocalIsoDate` returns null
        // instead of throwing the way `toISOString()` does.
        const goalDate = formatLocalIsoDate(getGoalDate(input))
        if (goalDate) pipe.setData(TARGET_GOAL_DATE_KEY, goalDate)

        const goalDate1kg = formatLocalIsoDate(
          getGoalDate1kg({ weightKg, targetWeightKg, goalPaceKg: 1 }),
        )
        if (goalDate1kg) pipe.setData(TARGET_GOAL_DATE_1KG_KEY, goalDate1kg)

        const fasterGoalDate = formatLocalIsoDate(getFasterProgressDate(input))
        if (fasterGoalDate) pipe.setData(FASTER_GOAL_DATE_KEY, fasterGoalDate)

        pipe.setData(ONE_MONTH_WEIGHT_LOSS_KG_KEY, getOneMonthWeightLossKg(input))
      },
      { immediate: true },
    )

    watch(
      () => [pipe.getData(WEIGHT_KEY), pipe.getData(TARGET_WEIGHT_KEY)],
      ([w, t]) => {
        const weightKg = Number(w)
        const targetWeightKg = Number(t)
        if (!Number.isFinite(weightKg) || weightKg <= 0) return
        if (!Number.isFinite(targetWeightKg) || targetWeightKg <= 0) return
        const input = { weightKg, targetWeightKg }
        pipe.setData(DESIRED_WEIGHT_DIFF_KG_KEY, getDesiredWeightDiffKg(input))
        pipe.setData(FIRST_WEEK_LOSS_KG_KEY, getFirstWeekLossKg(input))
        pipe.setData(FIRST_WEEK_DIFF_KG_KEY, getFirstWeekDiffKg(input))
        pipe.setData(PERCENT_OF_CURRENT_WEIGHT_KEY, percentOfCurrentWeight(input))
      },
      { immediate: true },
    )

    watch(
      () => pipe.getData(AGE_KEY),
      (raw) => {
        const decade = getAgeDecade({ age: Number(raw) })
        if (decade !== null) pipe.setData(AGE_GROUP_DECADE_KEY, decade)
      },
      { immediate: true },
    )

    // Time from "today" (the visitor's local date, read fresh on each change)
    // until the picked event date, plus the projected weight lost by then.
    // Skips while the date is unset so prerender (empty date) writes nothing.
    watch(
      () => [
        pipe.getData(EVENT_DATE_KEY),
        pipe.getData(WEIGHT_KEY),
        pipe.getData(TARGET_WEIGHT_KEY),
        pipe.getData(GOAL_PACE_KEY),
      ],
      ([eventDateValue, weightRaw, targetWeightRaw, paceRaw]) => {
        if (typeof eventDateValue !== 'string' || !eventDateValue) return
        const days = Math.max(0, getDaysUntil(eventDateValue))
        pipe.setData(DAYS_UNTIL_EVENT_KEY, days)
        pipe.setData(WEEKS_UNTIL_EVENT_KEY, getWeeksFromDays(days))
        pipe.setData(MONTHS_UNTIL_EVENT_KEY, getMonthsFromDays(days))

        const weightKg = Number(weightRaw)
        const targetWeightKg = Number(targetWeightRaw)
        if (!Number.isFinite(weightKg) || weightKg <= 0) return
        if (!Number.isFinite(targetWeightKg) || targetWeightKg <= 0) return
        const pace = Number(paceRaw)
        const goalPaceKg = Number.isFinite(pace) && pace > 0 ? pace : undefined
        pipe.setData(
          WEIGHT_LOST_BY_EVENT_KG_KEY,
          getWeightLostByWeeksKg({ weightKg, targetWeightKg, weeks: days / 7, goalPaceKg }),
        )
      },
      { immediate: true },
    )
  },
})

export default useFitnessFunctionsIntegration
